/** venture/Step15Report.tsx — 「정기·결산 보고」 본문 (조합 단위 · 결성등록 후 매년 반복, 2026-09-24 UX 개편)
 *  위: 결성등록일(8단계) + VICS 로그인 한 줄 → 「보고 일정」 연도별 칩(상태·D-day) → 지금 할 보고 카드(체크리스트·완료 버튼)
 *  결산은 카드 안에서 감사 면제 판정 → 자금운용현황 양식 채우기 / 결산서·감사의견서 업로드. 지난 보고는 접어 둔다. */
import React, { useState } from 'react';
import { getTxt, mutStore } from './stepcommon';
import VicsLogin from './VicsLogin';
import RepStrip, { dday } from './RepStrip';
import RepAnnual from './RepAnnual';
import { periods, regDateOf, isRegistered, todayYmd, REP_STAT_KO } from './report';
import type { RepPeriod } from './report';
import type { Project } from './types';

interface Props { p: Project; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }
const mmdd = (iso: string): string => iso.slice(5).replace('-', '/');

function HalfSteps({ x }: { x: RepPeriod }) {
  return (
    <ol className="repsteps">
      <li><b>VICS 로그인</b> → 메뉴 검색 <code>1329</code> 보고 검증 및 확정</li>
      <li>보고연월 확인 → <b>「검증」</b> · 오류가 나면 「오류확인」으로 해당 화면을 고치고 다시 검증</li>
      <li>오류 없으면 <b>「확정/전송」</b> → 아래 완료 표시</li>
      <li className="muted">투자·회수 변동이 있었다면 1313·1319·1320(13단계 화면)을 먼저 반영 · 보고 대상 {x.covers}</li>
    </ol>
  );
}

export default function Step15Report({ p, onPatch, toast }: Props) {
  const today = todayYmd();
  const reg = regDateOf(p);
  const list = periods(p, today);
  const todo = list.filter((x) => x.stat !== 'done');
  const doneList = list.filter((x) => x.stat === 'done');
  const lateList = todo.filter((x) => x.stat === 'late');
  const focus = todo.find((x) => x.stat === 'late') || todo.find((x) => x.stat === 'due') || todo[0];
  const [active, setActive] = useState<string>(focus ? focus.key : '');
  const [showDone, setShowDone] = useState(false);
  const toggle = (key: string, on: boolean) => onPatch(mutStore(p, 's15', (s) => { s.chk = s.chk || {}; s.chk[key] = on; }));
  const setReg = (v: string) => onPatch(mutStore(p, 's8', (s) => { s.txt = s.txt || {}; if (v) s.txt._regdate = v; else delete s.txt._regdate; }));
  const markAllLate = () => onPatch(mutStore(p, 's15', (s) => { const chk = s.chk || {}; lateList.forEach((x) => { chk[x.key] = true; }); s.chk = chk; }));
  const cur = list.find((x) => x.key === active) || focus;

  return (
    <div className="sact rep">
      {!isRegistered(p) ? <div className="s3file"><span className="s3muted">아직 조합 결성등록 전입니다 — 7단계 완료 후 보고 의무가 시작됩니다</span></div> : null}
      <div className="rephead">
        <div className="regdate">
          <span className="vk">📅 결성등록일</span>
          {reg.src === 'input'
            ? <b>{reg.date}</b>
            : <input className="cotext" type="date" value={getTxt(p, 's8', '_regdate')} onChange={(e) => setReg(e.target.value)} title="등록원부의 등록일자 — 8단계 조합 등록정보와 같은 값" />}
          {reg.src === 'input'
            ? <span className="s3muted">8단계 조합 등록정보 · 등록원부 등록일자</span>
            : <span className="pill p-amber" title="지금은 신청일/등록일로 임시 계산 중">{reg.date ? '임시 (' + (reg.src === 'apply' ? '마스터 신청일' : '프로젝트 등록일') + ' ' + reg.date + ')' : '입력 필요'} — 등록원부의 등록일자를 넣어 주세요</span>}
        </div>
        <VicsLogin p={p} toast={toast} compact />
      </div>
      <div className="snote">정기보고 <b>1/1~1/31 · 7/1~7/31</b> (VICS [1329] 확정/전송) · 결산보고 <b>1/1~3/31</b> ([1340] 결산서+감사의견서 또는 자금운용현황) · 기간이 오면 목록·헤더에 색으로 표시</div>

      {list.length ? <RepStrip list={list} today={today} activeKey={cur ? cur.key : ''} onPick={(k) => { setActive(k); if (doneList.some((x) => x.key === k)) setShowDone(true); }} /> : (
        <div className="s3file" style={{ marginTop: 8 }}><span className="s3muted">{reg.date ? '결성등록일(' + reg.date + ') 이후 첫 보고 기간이 아직 오지 않았습니다' : '결성등록일을 입력하면 보고 일정이 계산됩니다'}</span></div>
      )}
      {lateList.length >= 2 ? (
        <div className="s3file" style={{ marginTop: 8 }}>
          <span className="rf" style={{ whiteSpace: 'normal', flex: '1 1 260px' }}>⚠ 지연으로 보이는 보고 {lateList.length}건 — 이미 VICS에서 마친 것이면 한 번에 정리</span>
          <button type="button" className="rbtn" onClick={markAllLate}>지난 보고 모두 완료로 표시</button>
        </div>
      ) : null}

      {cur ? (
        <div className={'repcard ' + cur.stat}>
          <div className="repcard-h">
            <span className={'pill ' + (cur.stat === 'late' ? 'p-red' : cur.stat === 'due' ? 'p-amber' : cur.stat === 'done' ? 'p-green' : 'p-gray')}>{REP_STAT_KO[cur.stat]} · {dday(cur.from, cur.to, today, cur.stat)}</span>
            <b>{cur.label}</b>
            <span className="s3muted">{mmdd(cur.from)} ~ {mmdd(cur.to)} ({cur.to.slice(0, 4)}) · {cur.covers}</span>
            <span className="grow" />
            {cur.stat === 'done'
              ? <button type="button" className="chkpill sm on" onClick={() => toggle(cur.key, false)}>✓ 완료 (해제)</button>
              : cur.stat === 'upcoming'
                ? <span className="s3muted">{mmdd(cur.from)}부터 할 수 있어요</span>
                : <button type="button" className="rbtn ok" onClick={() => { toggle(cur.key, true); toast(cur.label + ' 완료로 표시했습니다'); }}>{cur.kind === 'annual' ? '결산보고 완료로 표시' : '확정/전송 완료로 표시'}</button>}
          </div>
          {cur.kind === 'annual' ? <RepAnnual p={p} y={cur.year} onPatch={onPatch} toast={toast} /> : <HalfSteps x={cur} />}
        </div>
      ) : null}

      {doneList.length ? (
        <div className="repdone">
          <button type="button" className="rbtn" onClick={() => setShowDone(!showDone)}>✅ 완료한 보고 {doneList.length}건 {showDone ? '접기 ▴' : '보기 ▾'}</button>
          {showDone ? doneList.map((x) => (
            <div className="s3file" key={x.key} style={{ marginTop: 6 }}>
              <span className="pill p-green">완료</span><b>{x.label}</b><span className="s3muted">{mmdd(x.from)} ~ {mmdd(x.to)} ({x.to.slice(0, 4)})</span><span className="grow" />
              {x.kind === 'annual' ? <button type="button" className="rbtn" onClick={() => setActive(x.key)}>첨부 다시 보기</button> : null}
              <button type="button" className="rbtn" onClick={() => toggle(x.key, false)}>완료 해제</button>
            </div>
          )) : null}
        </div>
      ) : null}
    </div>
  );
}
